import { Inject, Injectable } from '@angular/core';
import {
  AppConfiguration,
  AppConfigurationProvider
} from '@app-core/models/config';
import { IImageValidationResponse } from '@app-core/models/image-validation-response';
import { IImageValidationResult } from '@app-core/models/image-validation-result';
import { IMetric } from '@app-core/models/metric';
import { Relay42Service } from '@app-core/services/relay42.service';
import { FaceImage } from '@app-pages/models/face-image';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

/* eslint-disable @typescript-eslint/no-explicit-any */

@Injectable({
  providedIn: 'root'
})
export class ImageValidationService {
  private _apiUrl: string;
  private _isMock = !environment.production && environment.api.mock;
  private _fileExtension = this._isMock ? '.json' : '';
  private _imageUploader: FileUploader;

  private _onLoading = new Subject<boolean>();
  private _onError = new Subject<void>();
  private _onValidate = new Subject<IImageValidationResult>();

  constructor(
    private _translate: TranslateService,
    private _relay42Service: Relay42Service,
    @Inject(AppConfigurationProvider) private _config: AppConfiguration
  ) {
    this._apiUrl = this._isMock
      ? './assets/mock-data/pasfoto-api'
      : this._config.pasfoto_api_url;

    this._imageUploader = new FileUploader({
      url: `${this._apiUrl}/api/validate${
        this._fileExtension
      }?canConstructFacesUsingProfile=true`,
      method: this._isMock ? 'GET' : 'POST',
      allowedMimeType: [
        'image/bmp',
        'image/gif',
        'image/jpeg',
        'image/png',
        'image/tiff'
      ],
      autoUpload: false,
      maxFileSize: 10 * 1024 * 1024,
      queueLimit: 1
    });

    this._initUploadListeners();
  }

  public getImageUploader(): FileUploader {
    return this._imageUploader;
  }

  public onError$(): Observable<void> {
    return this._onError.asObservable();
  }

  public onLoading$(): Observable<boolean> {
    return this._onLoading.asObservable();
  }

  public onValidate$(): Observable<IImageValidationResult> {
    return this._onValidate.asObservable();
  }

  private _initUploadListeners(): void {
    this._imageUploader.onAfterAddingFile = (item: FileItem) => {
      this._onLoading.next(true);
      this._relay42Service.publishEvent(
        'userinteractie/start-upload',
        'start-upload',
        new Date()
      );
      item.withCredentials = false;
      item.upload();
    };

    this._imageUploader.onErrorItem = (item: FileItem) => {
      this._onLoading.next(false);
      this._onError.next(null);
      item.remove();
      this._relay42Service.publishEvent(
        'userinteractie/end-upload',
        'end-upload',
        new Date()
      );
    };

    this._imageUploader.onSuccessItem = (item: FileItem, res: string) => {
      this._onLoading.next(false);
      this._relay42Service.publishEvent(
        'userinteractie/end-upload',
        'end-upload',
        new Date()
      );

      const response =
        (JSON.parse(res)[0] as IImageValidationResponse) ||
        ({} as IImageValidationResponse);

      this._translateFailingMetrics(response).subscribe(
        (failingMetrics: IMetric[]) => {
          this._onValidate.next({
            compliant: !!response.compliant,
            image: response.compliant
              ? new FaceImage(response.faceAsBase64, item.file.type, false)
              : null,
            failingMetrics
          } as IImageValidationResult);
        }
      );

      item.remove();
    };
  }

  private _translateFailingMetrics(
    validationResponse: IImageValidationResponse
  ): Observable<IMetric[]> {
    if (validationResponse.compliant) return of(null);

    return of(validationResponse.failingMetrics).pipe(
      map((metrics: IMetric[]) => {
        return metrics.filter(
          (metric, position, metrics) =>
            metrics.map(m => m['metric']).indexOf(metric['metric']) ===
              position &&
            metrics
              .map(m => m['metricMessage'])
              .indexOf(metric['metricMessage']) === position
        );
      }),
      switchMap((filteredMetrics: IMetric[]) => {
        if (filteredMetrics.some(metric => metric.metricMessage == null)) {
          filteredMetrics = filteredMetrics.filter(m => m.metricMessage);
          filteredMetrics = [
            ...filteredMetrics,
            { metric: 'IMAGE-INVALID-DEFAULT' }
          ];
        }
        return forkJoin(
          filteredMetrics.map((metric: IMetric) => {
            return this._translate
              .get(`UPLOAD-PAGE.ERROR.${metric.metric}`)
              .pipe(
                map((translatedMessage: string) => {
                  return {
                    ...metric,
                    metricMessage: translatedMessage.includes(
                      'UPLOAD-PAGE.ERROR'
                    )
                      ? metric.metricMessage
                      : translatedMessage
                  };
                })
              );
          })
        );
      })
    );
  }
}
